import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Container, TextField, Typography, Paper, Alert, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useDispatch } from 'react-redux';
import { setLoadingStatus } from '../../redux/loading';
import { useTranslation } from 'react-i18next';
import { useChangePasswordMutation } from '../../api/graphql/generated/hooks';
import { setSnackbar, SnackbarStates } from '../../redux/snackbar';

const REDIRECT_TIMEOUT_SEC = 6;

interface PasswordResetProps {
  /**
   * Temporary active JWT token for password reset authentication
   */
  token: string;
}

export default function PasswordReset({ token }: PasswordResetProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const refreshToken = new URLSearchParams(window.location.search).get('refresh') || '';

  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const timeoutRef = useRef<number | NodeJS.Timeout | null>(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  dispatch(setLoadingStatus(false));

  useEffect(() => {
    return () => {
      // cleanup on unmount to avoid leaks
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const [changePasswordMut, { loading: changePasswordLoading }] = useChangePasswordMutation();

  useEffect(() => {
    dispatch(setLoadingStatus(changePasswordLoading));
  }, [changePasswordLoading, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('pages.resetPassword.passwordMismatch'));
      return;
    }

    try {
      // call change password mutation
      await changePasswordMut({
        variables: {
          input: {
            password,
            refreshToken,
          },
        },
        fetchPolicy: 'no-cache',
        context: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });
      dispatch(setSnackbar(true, SnackbarStates.SUCCESS, t('pages.resetPassword.successMessage')));
      // start redirect countdown
      timeoutRef.current = setInterval(() => {
        setRedirectCountdown((prev) => (prev || REDIRECT_TIMEOUT_SEC) - 1);
      }, 1000);
    } catch (err) {
      setError(t('pages.resetPassword.errorMessage'));
    }
  };

  useEffect(() => {
    if (redirectCountdown !== null && redirectCountdown <= 0) {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
        timeoutRef.current = null;
      }
      // reload current page without query params
      window.location.href = window.location.origin + window.location.pathname;
    }
  }, [redirectCountdown]);

  return (
    <Container
      maxWidth='sm'
      className='content'
    >
      <Box
        className='App'
        sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <Paper
          elevation={3}
          sx={{ p: 4, width: '100%' }}
        >
          <Typography
            component='h1'
            variant='h5'
            align='center'
            gutterBottom
          >
            {t('pages.resetPassword.title')}
          </Typography>
          <Typography
            component='p'
            variant='body2'
            align='center'
            gutterBottom
            color='text.secondary'
          >
            {t('pages.resetPassword.instructions')}
          </Typography>
          {error && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}
          {(redirectCountdown || 0) > 0 && (
            <Alert
              severity='success'
              sx={{ mb: 2 }}
            >
              {t('pages.resetPassword.successMessage')}
              {t('pages.resetPassword.redirectMessage', { countdown: redirectCountdown })}
            </Alert>
          )}
          <Box
            component='form'
            onSubmit={handleSubmit}
          >
            <TextField
              margin='normal'
              required
              type={showPassword ? 'text' : 'password'}
              fullWidth
              id='newPassword'
              label={t('pages.resetPassword.newPasswordLabel')}
              name='newPassword'
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge='end'
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              margin='normal'
              required
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              id='confirmPassword'
              label={t('pages.resetPassword.confirmPasswordLabel')}
              name='confirmPassword'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge='end'
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <Button
              type='submit'
              fullWidth
              className='btn'
              sx={{ mt: 3, mb: 2 }}
            >
              {t('pages.resetPassword.submitButton')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
